import { useState, useRef } from 'react';
import { X, Bug, Upload, Send } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { captureBugReportContext } from '@lnr-v2/utils';
import styles from './BugReportModal.module.css';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BugReportModal({
  isOpen,
  onClose,
}: BugReportModalProps) {
  const { showSuccess, showError } = useToast();
  const { user, hasStoredToken } = useAuth();
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please upload an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('Image must be smaller than 5MB');
        return;
      }

      setScreenshot(file);
    }
  };

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      showError('Please describe the bug');
      return;
    }

    setIsSubmitting(true);

    try {
      // Capture browser context
      const context = captureBugReportContext();

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('message', message.trim());
      formData.append('url', context.url);
      formData.append('userAgent', context.userAgent);
      formData.append('browser', context.browser);
      formData.append('os', context.os);
      formData.append('screenSize', context.screenSize);
      formData.append('viewport', context.viewport);

      // Add screenshot if provided
      if (screenshot) {
        formData.append('screenshot', screenshot);
      }

      // Verify authentication
      if (!user || !hasStoredToken()) {
        showError('You must be logged in to report bugs');
        return;
      }

      // Get auth token from either sessionStorage or localStorage
      const token =
        sessionStorage.getItem('adminToken') ||
        localStorage.getItem('adminToken');

      if (!token) {
        showError('Authentication token not found. Please log in again.');
        return;
      }

      // Send bug report
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/bug-report`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        // Handle specific error status codes
        if (response.status === 401) {
          throw new Error(
            'Your session has expired. Please log in again and try submitting the bug report.'
          );
        }

        if (response.status === 403) {
          throw new Error('You do not have permission to submit bug reports.');
        }

        // Try to parse error message from response
        try {
          const error = await response.json();
          throw new Error(
            error.error || error.message || 'Failed to send bug report'
          );
        } catch {
          throw new Error(
            `Failed to send bug report (Status: ${response.status})`
          );
        }
      }

      showSuccess('Bug report sent successfully!');

      // Reset form
      setMessage('');
      setScreenshot(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('Error sending bug report:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to send bug report'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.title}>
            <Bug size={24} className={styles.icon} />
            <h2>Signaler un bug</h2>
          </div>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Message */}
          <div className={styles.field}>
            <label htmlFor="bug-message" className={styles.label}>
              Décrivez le problème *
            </label>
            <textarea
              id="bug-message"
              className={styles.textarea}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Qu'est-ce qui ne fonctionne pas ?"
              rows={5}
              maxLength={5000}
              required
              disabled={isSubmitting}
            />
            <div className={styles.charCount}>
              {message.length} / 5000 caractères
            </div>
          </div>

          {/* Screenshot Upload */}
          <div className={styles.field}>
            <label className={styles.label}>Screenshot (optionnel)</label>
            {!screenshot ? (
              <div className={styles.uploadArea}>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="bug-screenshot"
                  className={styles.fileInput}
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="bug-screenshot" className={styles.uploadLabel}>
                  <Upload size={24} />
                  <span>Cliquez pour ajouter une capture d&apos;écran</span>
                  <span className={styles.uploadHint}>PNG, JPG (max 5MB)</span>
                </label>
              </div>
            ) : (
              <div className={styles.filePreview}>
                <div className={styles.fileInfo}>
                  <Upload size={20} />
                  <span>{screenshot.name}</span>
                  <span className={styles.fileSize}>
                    ({(screenshot.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.removeFile}
                  onClick={handleRemoveScreenshot}
                  disabled={isSubmitting}
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Info */}
          <p className={styles.info}>
            Les informations techniques (URL, navigateur, etc.) seront
            automatiquement incluses.
          </p>

          {/* Actions */}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting || !message.trim()}
            >
              {isSubmitting ? (
                'Envoi...'
              ) : (
                <>
                  <Send size={16} />
                  Envoyer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
